<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import AiWorkflowHelp from '@/shared/components/AiWorkflowHelp.vue'
  import { Button } from '@/components/ui/button'
  import { Label } from '@/components/ui/label'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import type { FetchDataOptions, PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
  } from '@/components/ui/dialog'
  import { type CustomerUser as User, CaseLifecycleWorkflowsService } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { PlusIcon, PencilIcon, TrashIcon, SettingsIcon } from 'lucide-vue-next'

  import * as yup from 'yup'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'

  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)
  const { fetchLawFields, getLawFieldOptions, getLawTopicOptions } = useLawFieldStore()

  // Translation setup
  const { t } = useTranslation({
    en: {
      title: 'AI Workflows',
      description:
        'Manage your AI workflows for different phases of the case lifecycle. These workflows define specific instructions for AI agents based on legal area and legal topic.',
      basicSettings: 'Basic Settings',
      createWorkflow: 'Create New Workflow',
      createFirstWorkflow: 'Create First Workflow',
      editWorkflow: 'Edit Workflow',
      loading: 'Loading workflows...',
      noWorkflows: 'No workflows created yet',
      general: 'General',
      name: 'Name',
      nameRequired: 'Name is required',
      nameMaxLength: 'Name may be a maximum of 255 characters long',
      namePlaceholder: 'e.g. Employment Law Standard Workflow',
      legalArea: 'Legal Area',
      legalTopic: 'Legal Topic',
      selectLegalArea: 'Select legal area (optional)',
      selectLegalTopic: 'Select legal topic (optional)',
      allLegalAreas: 'All Legal Areas',
      allLegalTopics: 'All Legal Topics',
      instructionsStep1: 'Instructions for Case Creation - Step 1',
      instructionsStep1Placeholder:
        'Enter instructions for AI agents during case creation (Step 1)...',
      instructionsStep2: 'Instructions for Case Creation - Step 2',
      instructionsStep2Placeholder:
        'Enter instructions for AI agents during case creation (Step 2)...',
      instructionsDataComplete: 'Instructions for Complete Data Collection',
      instructionsDataCompletePlaceholder:
        'Enter instructions for AI agents when all data has been collected...',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      delete: 'Delete',
      deleteConfirmation: 'Are you sure you want to delete this workflow?',
      workflowCreated: 'Workflow successfully created',
      workflowUpdated: 'Workflow successfully updated',
      workflowDeleted: 'Workflow successfully deleted',
      workflowCreateError: 'Error creating workflow',
      workflowUpdateError: 'Error updating workflow',
      workflowDeleteError: 'Error deleting workflow',
      workflowLoadError: 'Error loading workflows',
      instructionsTooLong: 'Instructions may be a maximum of 5000 characters long',
      dialogDescription: 'Configure workflow settings and instructions for AI agents.',
      updated: 'Updated',
      edit: 'Edit',
      noWorkflowsFound: 'No workflows found.',
      deleteWorkflow: 'Delete Workflow',
      deleteWorkflowConfirmation:
        'Are you sure you want to delete this workflow? This action cannot be undone.',
      confirmDelete: 'Delete'
    },
    de: {
      title: 'KI-Workflows',
      description:
        'Verwalten Sie Ihre KI-Workflows für verschiedene Phasen des Mandatslebenszyklus. Diese Workflows definieren spezifische Anweisungen für KI-Agenten basierend auf Rechtsgebiet und Rechtsthema.',
      basicSettings: 'Grundeinstellungen',
      createWorkflow: 'Neuen Workflow erstellen',
      createFirstWorkflow: 'Ersten Workflow erstellen',
      editWorkflow: 'Workflow bearbeiten',
      loading: 'Lade Workflows...',
      noWorkflows: 'Noch keine Workflows erstellt',
      general: 'Allgemein',
      name: 'Name',
      nameRequired: 'Name ist erforderlich',
      nameMaxLength: 'Name darf maximal 255 Zeichen lang sein',
      namePlaceholder: 'z.B. Arbeitsrecht Standard Workflow',
      legalArea: 'Rechtsgebiet',
      legalTopic: 'Rechtsthema',
      selectLegalArea: 'Rechtsgebiet auswählen (optional)',
      selectLegalTopic: 'Rechtsthema auswählen (optional)',
      allLegalAreas: 'Alle Rechtsgebiete',
      allLegalTopics: 'Alle Rechtsthemen',
      instructionsStep1: 'Anweisungen bei Mandatserstellung - Schritt 1',
      instructionsStep1Placeholder:
        'Geben Sie hier die Anweisungen für KI-Agenten bei der Mandatserstellung (Schritt 1) ein...',
      instructionsStep2: 'Anweisungen bei Mandatserstellung - Schritt 2',
      instructionsStep2Placeholder:
        'Geben Sie hier die Anweisungen für KI-Agenten bei der Mandatserstellung (Schritt 2) ein...',
      instructionsDataComplete: 'Anweisungen bei vollständiger Datensammlung',
      instructionsDataCompletePlaceholder:
        'Geben Sie hier die Anweisungen für KI-Agenten ein, wenn alle Daten gesammelt wurden...',
      cancel: 'Abbrechen',
      save: 'Speichern',
      saving: 'Speichern...',
      delete: 'Löschen',
      deleteConfirmation: 'Sind Sie sicher, dass Sie diesen Workflow löschen möchten?',
      workflowCreated: 'Workflow erfolgreich erstellt',
      workflowUpdated: 'Workflow erfolgreich aktualisiert',
      workflowDeleted: 'Workflow erfolgreich gelöscht',
      workflowCreateError: 'Fehler beim Erstellen des Workflows',
      workflowUpdateError: 'Fehler beim Aktualisieren des Workflows',
      workflowDeleteError: 'Fehler beim Löschen des Workflows',
      workflowLoadError: 'Fehler beim Laden der Workflows',
      instructionsTooLong: 'Anweisungen dürfen maximal 5000 Zeichen lang sein',
      dialogDescription:
        'Konfigurieren Sie die Workflow-Einstellungen und Anweisungen für KI-Agenten.',
      updated: 'Aktualisiert',
      edit: 'Bearbeiten',
      noWorkflowsFound: 'Keine Workflows gefunden.',
      deleteWorkflow: 'Workflow löschen',
      deleteWorkflowConfirmation:
        'Sind Sie sicher, dass Sie diesen Workflow löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmDelete: 'Löschen'
    }
  })

  // State
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const isDialogOpen = ref(false)
  const editingWorkflow = ref(null)
  const isSubmitting = ref(false)
  const showDeleteModal = ref(false)
  const workflowToDelete = ref(null)

  // Form data
  const formData = ref({
    name: '',
    law_field: '',
    law_topic: '',
    on_case_ready_for_onboarding_step_1_instructions: '',
    on_case_ready_for_onboarding_step_2_instructions: '',
    on_all_data_collected_instructions: ''
  })

  // Validation schema
  const validationSchema = yup.object({
    name: yup.string().required(t('nameRequired')).max(255, t('nameMaxLength')),
    law_field: yup.string(),
    law_topic: yup.string(),
    on_case_ready_for_onboarding_step_1_instructions: yup
      .string()
      .max(5000, t('instructionsTooLong')),
    on_case_ready_for_onboarding_step_2_instructions: yup
      .string()
      .max(5000, t('instructionsTooLong')),
    on_all_data_collected_instructions: yup.string().max(5000, t('instructionsTooLong'))
  })

  // Computed
  const lawFieldOptions = computed(() => {
    if (!user.value) return []
    return getLawFieldOptions(user.value)
  })

  const lawTopicOptions = computed(() => {
    return getLawTopicOptions(formData.value.law_field)
  })

  const isFormValid = computed(() => {
    try {
      validationSchema.validateSync(formData.value)
      return true
    } catch {
      return false
    }
  })

  const dialogTitle = computed(() => {
    return editingWorkflow.value ? t('editWorkflow') : t('createWorkflow')
  })

  onMounted(async () => {
    const fetching = [fetchLawFields()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchWorkflows = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    const response = await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    })

    return {
      results: response.results || [],
      next: response.next || null,
      previous: response.previous || null,
      count: response.count || 0
    }
  }

  const openCreateDialog = () => {
    editingWorkflow.value = null
    resetForm()
    isDialogOpen.value = true
  }

  const openEditDialog = (workflow) => {
    editingWorkflow.value = workflow
    formData.value = {
      name: workflow.name,
      law_field: workflow.law_field,
      law_topic: workflow.law_topic,
      on_case_ready_for_onboarding_step_1_instructions:
        workflow.on_case_ready_for_onboarding_step_1_instructions,
      on_case_ready_for_onboarding_step_2_instructions:
        workflow.on_case_ready_for_onboarding_step_2_instructions,
      on_all_data_collected_instructions: workflow.on_all_data_collected_instructions
    }
    isDialogOpen.value = true
  }

  const resetForm = () => {
    formData.value = {
      name: '',
      law_field: '',
      law_topic: '',
      on_case_ready_for_onboarding_step_1_instructions: '',
      on_case_ready_for_onboarding_step_2_instructions: '',
      on_all_data_collected_instructions: ''
    }
  }

  const handleSubmit = async () => {
    if (!isFormValid.value || isSubmitting.value) return

    try {
      isSubmitting.value = true

      if (editingWorkflow.value) {
        // Update existing workflow
        await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsPartialUpdate({
          uuid: editingWorkflow.value.uuid,
          requestBody: formData.value
        })

        genericSuccessToast(t('workflowUpdated'))
      } else {
        // Create new workflow
        await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsCreate({
          requestBody: formData.value
        })

        genericSuccessToast(t('workflowCreated'))
      }

      isDialogOpen.value = false
      resetForm()

      // Refresh the table data
      if (paginatedTableRef.value) {
        await paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error saving workflow:', error)
      const errorMsg = editingWorkflow.value ? t('workflowUpdateError') : t('workflowCreateError')
      genericErrorToast(errorMsg)
    } finally {
      isSubmitting.value = false
    }
  }

  const openDeleteModal = (workflow) => {
    workflowToDelete.value = workflow
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    workflowToDelete.value = null
  }

  const confirmDelete = async () => {
    if (workflowToDelete.value) {
      try {
        await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsDestroy({
          uuid: workflowToDelete.value.uuid
        })

        genericSuccessToast(t('workflowDeleted'))
        closeDeleteModal()

        // Refresh the table data
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        console.error('Error deleting workflow:', error)
        genericErrorToast(t('workflowDeleteError'))
      }
    }
  }

  const onLawFieldChange = (value) => {
    // The v-model already updates formData.value.law_field
    formData.value.law_topic = '' // Reset law topic when field changes
  }

  const goToBasicSettings = () => {
    // Navigate to the basic settings page
    window.location.href = '/app/settings/ai/case-lifecycle-workflows/basic-settings'
  }
</script>

<template>
  <SettingsPage v-if="user">
    <template #actions>
      <Button
        variant="outline"
        class="mr-2"
        @click="goToBasicSettings"
      >
        <SettingsIcon class="mr-2 h-4 w-4" />
        {{ t('basicSettings') }}
      </Button>
      <Button
        class="bg-purple-600 text-white hover:bg-purple-700"
        @click="openCreateDialog"
      >
        <PlusIcon class="mr-2 h-4 w-4" />
        {{ t('createWorkflow') }}
      </Button>
    </template>

    <div class="space-y-6">
      <div class="space-y-2">
        <h2 class="text-lg font-semibold text-gray-900">{{ t('title') }}</h2>
        <p class="text-sm text-gray-600">
          {{ t('description') }}
        </p>
      </div>

      <div class="space-y-4">
        <PaginatedTableView
          ref="paginatedTableRef"
          :fetchData="fetchWorkflows"
          searchableFields="workflow name, law field, law topic"
        >
          <template #table-head>
            <TableCell class="w-1/3 py-2 font-medium">{{ t('name') }}</TableCell>
            <TableCell class="w-1/4 py-2 font-medium">{{ t('legalArea') }}</TableCell>
            <TableCell class="w-1/4 py-2 font-medium">{{ t('legalTopic') }}</TableCell>
            <TableCell class="w-1/8 py-2 font-medium">{{ t('updated') }}</TableCell>
            <TableCell class="w-12 py-2"></TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="workflow in items"
              :key="workflow.uuid"
              class="cursor-pointer hover:bg-gray-50"
              @click="openEditDialog(workflow)"
            >
              <TableCell class="py-4">{{ workflow.name }}</TableCell>
              <TableCell class="py-1">{{ workflow.law_field || '-' }}</TableCell>
              <TableCell class="py-1">{{ workflow.law_topic || '-' }}</TableCell>
              <TableCell class="py-1">
                {{ new Date(workflow.updated_at).toLocaleDateString('de-DE') }}
              </TableCell>
              <TableCell
                class="py-1"
                @click.stop
              >
                <TableOptionsButton
                  :options="[
                    {
                      label: t('edit'),
                      onClick: () => openEditDialog(workflow)
                    },
                    {
                      label: t('delete'),
                      onClick: () => openDeleteModal(workflow)
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
          <template #empty-state>
            <div class="py-8 text-center">
              <p class="mb-4 text-gray-500">{{ t('noWorkflows') }}</p>
              <Button
                class="bg-purple-600 text-white hover:bg-purple-700"
                @click="openCreateDialog"
              >
                <PlusIcon class="mr-2 h-4 w-4" />
                {{ t('createFirstWorkflow') }}
              </Button>
            </div>
          </template>
        </PaginatedTableView>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{{ dialogTitle }}</DialogTitle>
          <DialogDescription>
            {{ t('dialogDescription') }}
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-6 py-4">
          <!-- Name on its own line -->
          <div class="space-y-2">
            <InputField
              v-model="formData.name"
              name="name"
              :label="t('name') + ' *'"
              :placeholder="t('namePlaceholder')"
              :disabled="isSubmitting"
            />
          </div>

          <!-- Law Field and Law Topic on one line -->
          <div class="flex flex-row gap-4">
            <!-- Law Field -->
            <div class="flex-1 space-y-2">
              <SelectField
                v-model="formData.law_field"
                name="law_field"
                :label="t('legalArea')"
                :options="lawFieldOptions"
                :select-option-text="t('selectLegalArea')"
                :allow-unselect="true"
                :unselected-option-text="t('allLegalAreas')"
                @update:model-value="onLawFieldChange"
              />
            </div>

            <!-- Law Topic -->
            <div class="flex-1 space-y-2">
              <SelectField
                v-model="formData.law_topic"
                name="law_topic"
                :label="t('legalTopic')"
                :options="lawTopicOptions"
                :select-option-text="t('selectLegalTopic')"
                :allow-unselect="true"
                :unselected-option-text="t('allLegalTopics')"
                :disabled="!formData.law_field"
              />
            </div>
          </div>

          <!-- Case Create Instructions - Step 1 -->
          <div class="space-y-2">
            <Label for="case_create_step_1_instructions">{{ t('instructionsStep1') }}</Label>
            <TextAreaField
              name="on_case_ready_for_onboarding_step_1_instructions"
              v-model="formData.on_case_ready_for_onboarding_step_1_instructions"
              :placeholder="t('instructionsStep1Placeholder')"
              :disabled="isSubmitting"
            />
            <AiWorkflowHelp />
          </div>

          <!-- Case Create Instructions - Step 2 -->
          <div class="space-y-2">
            <Label for="case_create_step_2_instructions">{{ t('instructionsStep2') }}</Label>
            <TextAreaField
              name="on_case_ready_for_onboarding_step_2_instructions"
              v-model="formData.on_case_ready_for_onboarding_step_2_instructions"
              :placeholder="t('instructionsStep2Placeholder')"
              :disabled="isSubmitting"
              :enable-text-styling-buttons="false"
              :enable-code-view-button="false"
              :custom-toolbar-buttons="['bold', 'italic', 'underline']"
            />
            <AiWorkflowHelp />
          </div>

          <!-- All Data Collected Instructions -->
          <div class="space-y-2">
            <Label for="all_data_collected_instructions">
              {{ t('instructionsDataComplete') }}
            </Label>
            <TextAreaField
              name="on_all_data_collected_instructions"
              v-model="formData.on_all_data_collected_instructions"
              :placeholder="t('instructionsDataCompletePlaceholder')"
              :disabled="isSubmitting"
              :enable-text-styling-buttons="false"
              :enable-code-view-button="false"
              :custom-toolbar-buttons="['bold', 'italic', 'underline']"
            />
            <AiWorkflowHelp />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            :disabled="isSubmitting"
            @click="isDialogOpen = false"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :disabled="!isFormValid || isSubmitting"
            class="bg-purple-600 text-white hover:bg-purple-700"
            @click="handleSubmit"
          >
            {{ isSubmitting ? t('saving') : t('save') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Delete Confirmation Modal -->
    <Dialog
      :open="showDeleteModal"
      @update:open="closeDeleteModal"
    >
      <DialogContent v-if="workflowToDelete">
        <DialogHeader>
          <DialogTitle>{{ t('deleteWorkflow') }}</DialogTitle>
          <DialogDescription>
            {{ t('deleteWorkflowConfirmation') }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            @click="closeDeleteModal"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            variant="destructive"
            @click="confirmDelete"
          >
            {{ t('confirmDelete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </SettingsPage>
</template>
